import { usePostSmsBericht } from "@/entities/bericht";
import { Contactpersoon } from "@/entities/contactpersoon";
import { ContactpersonenMultiSelect } from "@/features/contactpersonen";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Form,
    TextAreaFormField,
} from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { RadixBaseUiInteropContextProvider } from "@/shared/components/utils/radix-base-ui-interop-provider.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, SetStateAction, useRef } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

const maxMessageLength = 450;
const nieuwBerichtSchema = z.object({
    bericht: z
        .string()
        .trim()
        .nonempty({
            message: "Voer een bericht in.",
        })
        .max(maxMessageLength, { message: "Maximaal 450 tekens toegestaan." }),
    ontvangers: z.array(z.string(), { error: "Vul minimaal 1 ontvanger in" }).min(1, { error: "Vul minimaal 1 ontvanger in" }),
});

type NieuwSmsBerichtDialogProps = {
    dossierId: string;
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
    prefilledContactIds?: string[];
    allowCreateContactpersoon?: boolean;
};

type NieuwSmsBerichtData = z.infer<typeof nieuwBerichtSchema>;
export function NieuwSmsBerichtDialog({
    dossierId,
    open,
    onOpenChange,
    prefilledContactIds,
    allowCreateContactpersoon = true,
}: NieuwSmsBerichtDialogProps) {
    const postSpecifiekBericht = usePostSmsBericht();
    const dialogRef = useRef<HTMLDivElement | null>(null);

    const formRef = useRef<HTMLFormElement>(null);
    const form = useForm<NieuwSmsBerichtData>({
        resolver: zodResolver(nieuwBerichtSchema),
        defaultValues: {
            bericht: "",
            ontvangers: prefilledContactIds ?? [],
        },
    });

    const handleSubmit: SubmitHandler<NieuwSmsBerichtData> = (data) => {
        postSpecifiekBericht.mutate(
            { bericht: data.bericht, ontvangers: data.ontvangers, dossierId },
            { onSuccess: () => onOpenChange(false) }
        );
    };

    const addOntvanger = (contact: Contactpersoon) => {
        const ontvangers = form.getValues("ontvangers") ?? [];
        form.setValue("ontvangers", [...ontvangers, contact.id.toString()]);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <RadixBaseUiInteropContextProvider radixElement={dialogRef}>
                <DialogContainer className="min-w-[736px]" ref={dialogRef}>
                    <DialogHeader>
                        <DialogTitle>Nieuw medewerker bericht</DialogTitle>
                        <DialogDescription className="sr-only">Schrijf een bericht naar een medewerker</DialogDescription>
                    </DialogHeader>
                    <Form {...form}>
                        <form
                            ref={formRef}
                            onSubmit={(event) => {
                                // don't trigger submit here if the target was the contact form
                                if (event.target !== formRef.current) return;
                                void form.handleSubmit(handleSubmit)(event);
                            }}
                        >
                            <DialogBody className="grid gap-2">
                                <ContactpersonenMultiSelect
                                    form={form}
                                    name="ontvangers"
                                    dossierId={dossierId}
                                    onCreate={addOntvanger}
                                    allowCreateContactpersoon={allowCreateContactpersoon}
                                />
                                <TextAreaFormField
                                    form={form}
                                    label="Bericht"
                                    name="bericht"
                                    maxLength={maxMessageLength}
                                    className="h-25"
                                    autoFocus={prefilledContactIds && prefilledContactIds.length > 0}
                                />
                                {postSpecifiekBericht.error && (
                                    <ErrorDisplay error={postSpecifiekBericht.error} title="Fout bij het versturen van bericht" />
                                )}
                            </DialogBody>
                            <DialogFooter>
                                <Button type="button" variant="tertiary" onClick={() => onOpenChange(false)}>
                                    Annuleren
                                </Button>
                                <Button type="submit" variant="primary" disabled={postSpecifiekBericht.isPending}>
                                    Versturen
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContainer>
            </RadixBaseUiInteropContextProvider>
        </Dialog>
    );
}
